import type { CapId } from "./capabilities.js";

/**
 * The closed UI vocabulary (§5.1).
 *
 * Spec note: §5.1 budgets "nineteen node types" but enumerates eighteen. The
 * enumeration is the contract; the nineteenth slot is left unspent. Adding to
 * this list is a protocol change, not an app change — reach for `Surface`
 * instead (§11).
 */
export const NODE_TYPES = [
  // layout
  "Box",
  "Stack",
  "Split",
  "Toolbar",
  "Divider",
  "Spacer",
  // content
  "Text",
  "Heading",
  "Icon",
  "ObjectRef",
  // input
  "Button",
  "TextField",
  "TextArea",
  "Checkbox",
  "Select",
  // collections
  "List",
  "ListItem",
  // escape hatch
  "Surface",
] as const;

export type NodeType = (typeof NODE_TYPES)[number];

export const NODE_TYPE_SET: ReadonlySet<string> = new Set<string>(NODE_TYPES);

export type Node = {
  type: NodeType;
  /** Stable across renders; required on every node and unique among siblings. */
  key: string;
  props: Record<string, unknown>;
  children?: Node[];
};

/** Guard rails for a hostile or looping app. Enforced by `decodeNode`. */
export const NODE_LIMITS = {
  maxDepth: 64,
  maxNodes: 20_000,
} as const;

/** The closed icon set. Unknown names render as a neutral placeholder. */
export const ICON_NAMES = [
  "app",
  "check",
  "chevron-down",
  "chevron-right",
  "close",
  "collection",
  "document",
  "folder",
  "gear",
  "grip",
  "note",
  "object",
  "plus",
  "search",
  "terminal",
  "trash",
  "warning",
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export type Axis = "row" | "column";
export type Align = "start" | "center" | "end" | "stretch" | "baseline";
export type Justify = "start" | "center" | "end" | "between";
export type Tone = "default" | "muted" | "accent" | "danger" | "success";
export type TextSize = "xs" | "sm" | "md" | "lg";

/**
 * Advisory prop shapes. The wire type is `Record<string, unknown>` (§5.1); the
 * shell reads props defensively and the SDK's builders produce these. Documented
 * here so both sides agree on one vocabulary.
 */
export interface NodeProps {
  Box: {
    padding?: number;
    gap?: number;
    grow?: boolean;
    scroll?: boolean;
    background?: "none" | "subtle" | "raised";
    border?: boolean;
    radius?: number;
  };
  Stack: {
    direction?: Axis;
    gap?: number;
    padding?: number;
    align?: Align;
    justify?: Justify;
    wrap?: boolean;
    grow?: boolean;
    scroll?: boolean;
  };
  /** Exactly two children; `ratio` is the first child's share of the axis. */
  Split: { direction?: Axis; ratio?: number };
  Toolbar: { gap?: number };
  Divider: { direction?: Axis };
  /** Fixed `size` in px, or a flexible gap when omitted. */
  Spacer: { size?: number };

  Text: { text: string; tone?: Tone; size?: TextSize; mono?: boolean; bold?: boolean; wrap?: boolean };
  Heading: { text: string; level?: 1 | 2 | 3 };
  Icon: { name: string; size?: number; tone?: Tone };
  /** Drag source and "Open with…" target. Transfers the capability, never content. */
  ObjectRef: { capability: CapId; objectType: string; label: string; icon?: string };

  Button: {
    label: string;
    variant?: "default" | "primary" | "ghost" | "danger";
    icon?: string;
    disabled?: boolean;
  };
  TextField: { value: string; placeholder?: string; label?: string; disabled?: boolean; mono?: boolean };
  TextArea: { value: string; placeholder?: string; label?: string; rows?: number; disabled?: boolean; mono?: boolean };
  Checkbox: { checked: boolean; label?: string; disabled?: boolean };
  Select: {
    value: string;
    options: readonly { value: string; label: string }[];
    label?: string;
    disabled?: boolean;
  };

  List: { selectedKey?: string; grow?: boolean; scroll?: boolean };
  ListItem: { label?: string; icon?: string; detail?: string; selected?: boolean; disabled?: boolean };

  /** Raw rendering escape hatch. The shell owns each known `kind`. */
  Surface: { kind: string; stream?: CapId };
}

/**
 * Events each node type emits, as `{ t: "event", key, event }` (§5.2).
 * Payload shapes are documented alongside.
 */
export const NODE_EVENTS = {
  Button: ["press"],
  TextField: ["input", "submit"],
  TextArea: ["input"],
  Checkbox: ["change"],
  Select: ["change"],
  ListItem: ["activate", "select"],
  ObjectRef: ["activate", "drop"],
  Surface: ["ready"],
} as const;

export type InputEventPayload = { value: string };
export type ChangeEventPayload = { checked: boolean };
export type SelectEventPayload = { value: string };
